// externals
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// types
import type { VehicleSpecificationsSection } from '../store/selectors.types';
import { VppInventoryType } from '@makemydeal/dr-platform-types';

// store
import { vehicleDetailsSelectors } from '../store';

// constants
import { ACTIVE_INVENTORY_STATUS } from '../constants';

/**
 * Hook for vehicle specifications section.
 * Returns the full specifications object from `getVehicleSpecifications`.
 */
export const useVehicleSpecifications = (): VehicleSpecificationsSection => {
    const specifications = useSelector(vehicleDetailsSelectors.getVehicleSpecifications);
    const isAllVehicleInventoryFromCVIEnabled = useSelector(
        vehicleDetailsSelectors.additionalSelectors.getIsAllVehicleInventoryFromCVIEnabled
    );

    return useMemo(() => {
        const savings = specifications.savings ?? 0;
        const isSavings = savings >= 0;
        const savingsLabel = isSavings ? 'Your Discount' : 'Market Adjustment';
        const savingsText = isSavings ? `-$${savings.toLocaleString()}` : `$${Math.abs(savings).toLocaleString()}`;
        const isNew = specifications.condition === VppInventoryType.NEW;

        // TODO: This comment and the Toyota TSRP labeling logic below should be moved to
        // `dr-services-vehicle-lambda` so the price label is resolved at the data source.
        // Toyota uses "TSRP" (Total/Toyota Suggested Retail Price) instead of "MSRP". Unlike the
        // base-vehicle MSRP, TSRP is the fuller window-sticker total (regional accessories, packages
        // and delivery fees), so we surface "TSRP" as the price label for Toyota.
        const isToyota = specifications.make?.toLowerCase() === 'toyota';
        const msrpLabel = isNew && isToyota ? 'TSRP' : 'Market Price';

        const showSellingPrice = !!specifications.sellingPriceValue;
        const showMsrp = !!specifications.msrpValue && !(showSellingPrice && savings === 0);
        const showSavings = showMsrp && showSellingPrice;
        const shouldShowPricing = showMsrp || showSellingPrice;

        // Every status but ACTIVE (including a custom build's always-missing status, and a real VIN
        // that's simply absent from CVI) counts as "Not in Stock" per product decision in
        // https://coxauto.slack.com/archives/C0B509YN3NJ/p1790163461810559.
        const isNotInStock = isAllVehicleInventoryFromCVIEnabled && specifications.inventoryStatus !== ACTIVE_INVENTORY_STATUS;

        return {
            ...specifications,
            title: `${specifications.condition ?? ''} ${specifications.year ?? ''} ${specifications.make ?? ''} ${
                specifications.model ?? ''
            }`.trim(),
            isSavings,
            savingsLabel,
            savingsText,
            msrpLabel,
            showMsrp,
            showSavings,
            showSellingPrice,
            isNotInStock,
            shouldShowPricing
        };
    }, [specifications, isAllVehicleInventoryFromCVIEnabled]);
};
